/**
 * Hindi interface strings — the complete dictionary.
 *
 * Keys mirror `en.ts`; any key absent here falls back to English in
 * `translate`. Setup and lookup logic lives in `i18n.ts`.
 */

type Dict = Record<string, string>

export const hi: Dict = {
  /* --- common ------------------------------------------------------------ */
  'common.saveChanges': 'परिवर्तन सहेजें',
  'common.save': 'सहेजें',
  'common.cancel': 'रद्द करें',
  'common.saved': 'सहेजा गया',
  'common.add': 'जोड़ें',
  'common.adding': 'जोड़ा जा रहा है…',
  'common.delete': 'हटाएँ',
  'common.remove': 'हटाएँ',
  'common.edit': 'संपादित करें',
  'common.close': 'बंद करें',
  'common.closePanel': 'पैनल बंद करें',
  'common.dismiss': 'खारिज करें',
  'common.export': 'निर्यात',
  'common.exportCsv': 'CSV निर्यात करें',
  'common.viewAll': 'सभी देखें',
  'common.tryAgain': 'फिर से प्रयास करें',
  'common.retry': 'पुनः प्रयास',
  'common.loading': 'लोड हो रहा है…',
  'common.loadingPeople': 'लोग लोड हो रहे हैं…',
  'common.none': 'कोई नहीं',
  'common.never': 'कभी नहीं',
  'common.search': 'खोज',
  'common.searchEllipsis': 'खोजें…',
  'common.clearSearch': 'खोज साफ़ करें',
  'common.clear': 'साफ़ करें',
  'common.clearFilters': 'फ़िल्टर साफ़ करें',
  'common.beta': 'बीटा',
  'common.pdf': 'PDF',
  'common.connect': 'कनेक्ट करें',
  'common.disconnect': 'डिस्कनेक्ट करें',
  'common.reconnect': 'फिर से कनेक्ट करें',
  'common.active': 'सक्रिय',
  'common.restore': 'पुनर्स्थापित करें',
  'common.archive': 'संग्रह करें',
  'common.archived': 'संग्रहित',
  'common.confirm': 'पुष्टि करें',
  'common.signIn': 'साइन इन करें',
  'common.or': 'या',
  'common.couldNotSave': 'सहेजा नहीं जा सका',
  'common.couldNotSaveBody': 'आपके परिवर्तन संग्रहित नहीं हुए। फिर से प्रयास करें।',
  'common.somethingWentWrong': 'कुछ गड़बड़ हो गई। फिर से प्रयास करें।',

  /* --- time ------------------------------------------------------------- */
  'time.today': 'आज',
  'time.yesterday': 'कल (बीता)',
  'time.tomorrow': 'कल (आगामी)',
  'time.ago': '{value} पहले',

  /* --- navigation ----------------------------------------------------------- */
  'nav.section.insights': 'अंतर्दृष्टि',
  'nav.section.workspace': 'कार्यस्थल',
  'nav.section.admin': 'प्रशासन',
  'nav.item.dashboard': 'होम',
  'nav.item.meetings': 'मीटिंग',
  'nav.item.actions': 'कार्य सूची',
  'nav.item.ask': 'सभा से पूछें',
  'nav.item.search': 'खोज',
  'nav.item.analytics': 'विश्लेषण',
  'nav.item.integrations': 'एकीकरण',
  'nav.item.settings': 'सेटिंग्स',
  'nav.item.tenants': 'टेनेंट',
  'nav.item.audit': 'ऑडिट लॉग',
  'nav.item.vocabulary': 'शब्दावली',
  'nav.collapseSidebar': 'साइडबार संकुचित करें',
  'nav.expandSidebar': 'साइडबार विस्तृत करें',
  'nav.openNavigation': 'नेविगेशन खोलें',
  'nav.skipToContent': 'सामग्री पर जाएँ',
  'nav.primary': 'मुख्य',
  'nav.restoringSession': 'आपका सत्र पुनर्स्थापित किया जा रहा है',

  /* --- topbar / menus ------------------------------------------------------- */
  'topbar.searchWide': 'मीटिंग, लोग, निर्णय खोजें…',
  'palette.placeholder': 'मीटिंग खोजें या किसी पृष्ठ पर जाएँ…',
  'palette.inputAria': 'कमांड पैलेट इनपुट',
  'palette.dialogLabel': 'कमांड पैलेट',
  'palette.noMatches': 'कोई मेल नहीं',
  'palette.group.navigate': 'नेविगेट करें',
  'palette.group.meetings': 'मीटिंग',
  'palette.group.search': 'खोज',
  'palette.goTo': '{label} पर जाएँ',
  'palette.searchFor': '“{term}” के लिए ट्रांसक्रिप्ट खोजें',
  'topbar.home': '{app} होम',
  'userMenu.accountMenu': 'खाता मेन्यू',
  'userMenu.yourProfile': 'आपकी प्रोफ़ाइल',
  'userMenu.workspaceSettings': 'कार्यस्थल सेटिंग्स',
  'userMenu.appearance': 'दिखावट: {theme}',
  'userMenu.contactSupport': 'सहायता से संपर्क करें',
  'userMenu.signOut': 'साइन आउट',
  'theme.heading': 'दिखावट',
  'theme.change': 'दिखावट बदलें',
  'theme.light': 'लाइट',
  'theme.dark': 'डार्क',
  'theme.system': 'सिस्टम के अनुसार',
  'theme.value.light': 'लाइट',
  'theme.value.dark': 'डार्क',
  'theme.value.system': 'सिस्टम',
  'tenant.workspace': 'कार्यस्थल',
  'notifications.title': 'सूचनाएँ',
  'notifications.withUnread': 'सूचनाएँ ({count} अपठित)',
  'notifications.caughtUpTitle': 'आप पूरी तरह अपडेट हैं',
  'notifications.caughtUpBody': 'सारांश, असाइनमेंट और रिकॉर्डिंग विफलताएँ यहाँ आती हैं।',
  'notifications.markAllRead': 'सभी को पढ़ा हुआ चिह्नित करें',
  'notifications.summaryReady.body': 'सारांश और कार्रवाई आइटम पढ़ने के लिए तैयार हैं।',
  'notifications.leadIn.recordingFailed': 'रिकॉर्डिंग विफल',
  'notifications.leadIn.recordingStopped': 'रिकॉर्डिंग रुकी',
  'notifications.leadIn.summaryReady': 'सारांश तैयार',
  'notifications.recordingFailure.botNotAdmitted':
    'बॉट को समय रहते बैठक में प्रवेश नहीं दिया गया।',
  'notifications.recordingFailure.botDenied':
    'Sabha AI को बैठक में प्रवेश की अनुमति नहीं दी गई।',
  'notifications.recordingFailure.meetingCancelled':
    'रिकॉर्डिंग शुरू होने से पहले ही बैठक रद्द कर दी गई।',
  'notifications.recordingFailure.botRemoved':
    'बॉट को बैठक से हटा दिया गया, इसलिए रिकॉर्डिंग रुक गई।',
  'notifications.recordingFailure.platformUnreachable':
    'Sabha AI बैठक में शामिल नहीं हो सका। कृपया पुनः प्रयास करें।',
  'notifications.recordingFailure.genericError':
    'एक अप्रत्याशित त्रुटि के कारण रिकॉर्डिंग विफल हो गई।',
  'notifications.recordingFailure.unknownFailure':
    'हम रिकॉर्डिंग पूरी नहीं कर सके। कृपया पुनः प्रयास करें।',

  /* --- QueryBoundary / EmptyState defaults -------------------------------- */
  'query.loadFailTitle': 'हम इसे लोड नहीं कर सके',
  'query.noAccessTitle': 'आपके पास इसकी पहुँच नहीं है',
  'query.loadFailBody': 'अनुरोध सर्वर तक नहीं पहुँचा। अपना कनेक्शन जाँचें और फिर से प्रयास करें।',
  'query.noAccessBody': 'किसी कार्यस्थल व्यवस्थापक से पहुँच देने के लिए कहें।',
  'query.nothingHere': 'अभी यहाँ कुछ नहीं है',

  /* --- pagination ------------------------------------------------------- */
  'pagination.noResults': 'कोई परिणाम नहीं',
  'pagination.range': '{total} में से {from}–{to}',
  'pagination.previous': 'पिछला',
  'pagination.next': 'अगला',

  /* --- settings shell -------------------------------------------------------- */
  'settings.title': 'सेटिंग्स',
  'settings.description': 'कार्यस्थल कॉन्फ़िगरेशन, लोग और डेटा नियंत्रण।',
  'settings.nav': 'सेटिंग्स',
  'settings.tab.profile': 'आपकी प्रोफ़ाइल',
  'settings.tab.workspace': 'कार्यस्थल',
  'settings.tab.members': 'सदस्य',
  'settings.tab.roles': 'भूमिकाएँ',
  'settings.tab.patrons': 'ग्राहक',
  'settings.tab.recording': 'रिकॉर्डिंग नियम',
  'settings.tab.languages': 'भाषाएँ',
  'settings.tab.notifications': 'सूचनाएँ',
  'settings.tab.billing': 'बिलिंग',
  'settings.tab.security': 'सुरक्षा और डेटा',

  /* --- profile pane ------------------------------------------------------- */
  'profile.card.title': 'आपकी प्रोफ़ाइल',
  'profile.card.description': 'इस कार्यस्थल में आप दूसरों को कैसे दिखते हैं।',
  'profile.field.name': 'पूरा नाम',
  'profile.field.email': 'कार्य ईमेल',
  'profile.field.email.hint': 'आपके पहचान प्रदाता द्वारा प्रबंधित।',
  'profile.field.timezone': 'समय क्षेत्र',
  'profile.field.timezone.hint': 'ऐप में हर समय इसी क्षेत्र में दिखाया जाता है।',
  'profile.field.language': 'इंटरफ़ेस भाषा',
  'profile.field.language.hint': 'ट्रांसक्रिप्शन भाषाएँ नहीं बदलता।',
  'profile.personalEmail.title': 'व्यक्तिगत ईमेल',
  'profile.personalEmail.description': 'सभा आपको सीधे क्या भेजता है।',
  'profile.dailyDigest.label': 'छूटी हुई मीटिंग का दैनिक सारांश',
  'profile.dailyDigest.description': 'आपके समय क्षेत्र में सुबह 08:00 बजे एक ईमेल।',
  'profile.actionAssigned.label': 'Sabha Catchup: मेरे खुले कार्य',
  'profile.actionAssigned.description': 'सप्ताह के दिनों में शाम को (आपके समय क्षेत्र में 18:00) एक ईमेल — आपके खुले कार्य, नए कार्य, और आपकी आयोजित मीटिंग जिनके कार्यों का कोई स्वामी नहीं है। कुछ बताने को न हो तो ईमेल नहीं भेजा जाता।',

  /* --- dashboard --------------------------------------------------------- */
  'dashboard.greeting': 'नमस्ते, {name}',
  'dashboard.greetingFallback': 'वहाँ',
  'dashboard.subtitle': 'एक नज़र में आपका कार्यस्थल।',
  'dashboard.askSabha': 'सभा से पूछें',
  'dashboard.recordingNow': 'अभी रिकॉर्ड हो रहा है',
  'dashboard.startedAt': '{time} बजे शुरू हुआ',
  'dashboard.stat.meetingsRecorded': 'रिकॉर्ड की गई मीटिंग',
  'dashboard.stat.hoursCaptured': 'कैप्चर किए गए घंटे',
  'dashboard.stat.actionItemsOpen': 'खुले कार्य',
  'dashboard.stat.avgMeeting': 'औसत मीटिंग',
  'dashboard.stat.last30': 'पिछले 30 दिनों की तुलना में',
  'dashboard.recentMeetings': 'हाल की मीटिंग',
  'dashboard.noMeetingsTitle': 'अभी कोई मीटिंग नहीं',
  'dashboard.noMeetingsBody': 'अपने कैलेंडर की किसी मीटिंग में रिकॉर्डर को आमंत्रित करें और वह यहाँ दिखाई देगी।',
  'dashboard.yourActionItems': 'आपके कार्य',
  'dashboard.seeAll': 'सभी देखें',
  'dashboard.nothingAssigned': 'आपको कुछ भी असाइन नहीं किया गया',
  'dashboard.recordingVolume': 'रिकॉर्डिंग मात्रा',
  'dashboard.recordingVolumeDesc': 'प्रति दिन कैप्चर किए गए मिनट, पिछले 30 दिन।',
  'dashboard.upNext': 'आगे',
  'dashboard.upNextDesc': 'जिन मीटिंग में रिकॉर्डर शामिल होगा।',
  'dashboard.checkedRelative': '{when} जाँचा गया',
  'dashboard.checkForInvites': 'नए आमंत्रणों के लिए कैलेंडर जाँचें',
  'dashboard.refreshUpcoming': 'आगामी मीटिंग रीफ़्रेश करें',
  'dashboard.badge.joining': 'शामिल हो रहा है',
  'dashboard.badge.claimed': 'दावा किया गया',
  'dashboard.calendarNotConnected': 'कैलेंडर कनेक्ट नहीं है',
  'dashboard.calendarNotConnectedBody': 'यह परिनियोजन रिकॉर्डर का कैलेंडर नहीं पढ़ सकता, इसलिए केवल वही मीटिंग सूचीबद्ध हो सकती हैं जिनका उसने पहले ही दावा कर लिया है।',
  'dashboard.calendarUnreachable': 'कैलेंडर तक नहीं पहुँच सके',
  'dashboard.calendarUnreachableBody': 'आपकी डायरी से कुछ भी गायब नहीं है — हम बस उसे पढ़ नहीं सके। रीफ़्रेश करके देखें।',
  'dashboard.nothingScheduled': 'कुछ भी निर्धारित नहीं',
  'dashboard.calendarClear': 'आपका कैलेंडर खाली है।',
  'dashboard.staleCalendar': 'कैलेंडर अनुपलब्ध था, इसलिए यह सूची पुरानी हो सकती है।',

  /* --- meetings list ---------------------------------------------------------- */
  'meetings.title': 'मीटिंग',
  'meetings.description': 'हर वह मीटिंग जिसमें रिकॉर्डर को आमंत्रित किया गया था, उनमें वे भी जो रिकॉर्ड नहीं हो सकीं।',
  'meetings.recordMeeting': 'एक मीटिंग रिकॉर्ड करें',
  'meetings.recordNow': 'अभी रिकॉर्ड करें',
  'meetings.searchPlaceholder': 'शीर्षक, प्रतिभागी या मीटिंग ID से खोजें…',
  'meetings.searchAria': 'मीटिंग खोजें',
  'meetings.noMatchTitle': 'इन फ़िल्टरों से कोई मीटिंग मेल नहीं खाती',
  'meetings.noMatchBody': 'sabha.ai@pssgway.com को किसी मीटिंग में आमंत्रित करें और वह शुरू होने से लगभग दो मिनट पहले यहाँ दिखाई देगी।',
  'meetings.filter.status': 'स्थिति',
  'meetings.filter.platform': 'प्लेटफ़ॉर्म',
  'meetings.status.all': 'सभी स्थितियाँ',
  'meetings.status.ready': 'तैयार',
  'meetings.status.processing': 'प्रोसेसिंग',
  'meetings.status.recording': 'अभी रिकॉर्डिंग',
  'meetings.status.scheduled': 'निर्धारित',
  'meetings.status.failed': 'विफल',
  'meetings.status.denied': 'अस्वीकृत',
  'meetings.status.notAdmitted': 'प्रवेश नहीं मिला',
  'meetings.status.joinFailed': 'शामिल नहीं हो सका',
  'meetings.status.meetingCancelled': 'मीटिंग रद्द की गई',
  'meetings.status.skipped': 'रिकॉर्ड नहीं हुई',
  'meetings.status.noAudio': 'कोई ऑडियो नहीं',
  'meetings.platform.all': 'सभी प्लेटफ़ॉर्म',
  'meetings.shared': 'साझा',
  'meetings.recording': 'रिकॉर्डिंग',
  'meetings.decisions': '{count} निर्णय',
  'meetings.actions': '{count} कार्य',

  /* --- meeting status badge --------------------------------------------- */
  'meetingStatus.scheduled': 'निर्धारित',
  'meetingStatus.joining': 'शामिल हो रहा है',
  'meetingStatus.recording': 'रिकॉर्डिंग',
  'meetingStatus.processing': 'प्रोसेसिंग',
  'meetingStatus.ready': 'तैयार',
  'meetingStatus.failed': 'विफल',
  'meetingStatus.skipped': 'रिकॉर्ड नहीं हुई',
  'meetingStatus.noAudio': 'कोई ऑडियो नहीं',
  'meetingStatus.notAdmitted': 'प्रवेश नहीं मिला',
  'meetingStatus.denied': 'पहुंच अस्वीकृत',
  'meetingStatus.joinFailed': 'जुड़ नहीं सका',
  'meetingStatus.meetingCancelled': 'मीटिंग रद्द की गई',

  /* --- meeting detail --------------------------------------------------- */
  'meetingDetail.allMeetings': 'सभी मीटिंग',
  'meetingDetail.backToSeries': 'सीरीज़ पर वापस जाएँ',
  'meetingDetail.recorded': '{duration} रिकॉर्ड किया गया',
  'meetingDetail.meetingId': 'मीटिंग ID {id}',
  'meetingDetail.copyMeetingId': 'मीटिंग ID कॉपी करें',
  'meetingDetail.toast.meetingIdCopied': 'मीटिंग ID कॉपी हो गई',
  'meetingDetail.share': 'साझा करें',
  'meetingDetail.moreActions': 'और क्रियाएँ',
  'meetingDetail.reRunTranscription': 'ट्रांसक्रिप्शन फिर से चलाएँ',
  'meetingDetail.reSendSummaryEmail': 'सारांश ईमेल फिर से भेजें',
  'meetingDetail.deleteMeetingAndAudio': 'मीटिंग और ऑडियो हटाएँ',
  'meetingDetail.rejoin': 'बॉट को फिर से जुड़ने के लिए कहें',
  'meetingDetail.rejoinHint': 'होस्ट ने इस मीटिंग के लिए रिकॉर्डर को मना कर दिया था। फिर से कहने पर वह दोबारा लॉबी में पहुँचेगा।',
  'meetingDetail.toast.rejoinRequested': 'बॉट को फिर से जुड़ने के लिए कहा गया',
  'meetingDetail.toast.rejoinRequestedBody': 'यह लगभग एक मिनट में दस्तक देगा। मीटिंग में किसी को इसे अनुमति देनी होगी।',
  'meetingDetail.toast.rejoinAlreadyRequested': 'एक रिकॉर्डर पहले से ही इस मीटिंग में लौट रहा है।',
  'meetingDetail.toast.rejoinLimitReached': 'बॉट को पहले ही कई बार कहा जा चुका है। ज़रूरत हो तो मैनुअल रिकॉर्डिंग शुरू करें।',
  'meetingDetail.toast.rejoinMeetingOver': 'यह मीटिंग समाप्त हो चुकी है, इसलिए रिकॉर्डर अब इसमें शामिल नहीं हो सकता।',
  'meetingDetail.toast.rejoinFailed': 'बॉट को फिर से जुड़ने के लिए नहीं कहा जा सका',
  'meetingDetail.tab.summary': 'सारांश',
  'meetingDetail.tab.transcript': 'ट्रांसक्रिप्ट',
  'meetingDetail.tab.cleaned': 'साफ़ किया गया',
  'meetingDetail.tab.actions': 'कार्य सूची',
  'meetingDetail.tab.participants': 'प्रतिभागी',
  'meetingDetail.participantsTitle': 'प्रतिभागी',
  'meetingDetail.participantsDesc': 'बोलने का हिस्सा निर्दिष्ट ट्रांसक्रिप्ट से मापा जाता है, इसलिए बिना नाम वाली आवाज़ को छोड़ दिया जाता है।',
  'meetingDetail.noEmailCaptured': 'कोई ईमेल कैप्चर नहीं हुआ',
  'meetingDetail.organiser': 'आयोजक',
  'meetingDetail.external': 'बाहरी',
  'meetingDetail.deleteTitle': 'इस मीटिंग को हटाएँ?',
  'meetingDetail.deleteBody': 'रिकॉर्डिंग, ट्रांसक्रिप्ट और सारांश स्थायी रूप से हटा दिए जाते हैं। जो कार्य पहले ही अन्य टूल में भेजे जा चुके हैं वे नहीं हटते।',
  'meetingDetail.deletePermanently': 'स्थायी रूप से हटाएँ',
  'meetingDetail.toast.summaryEmailQueued': 'सारांश ईमेल कतार में',
  'meetingDetail.toast.queuedForReprocessing': 'फिर से प्रोसेस करने के लिए कतार में',
  'meetingDetail.toast.couldNotQueueReprocessing': 'फिर से प्रोसेसिंग कतार में नहीं डाल सके',
  'meetingDetail.toast.shareLinkCopied': 'साझा लिंक कॉपी किया गया',
  'meetingDetail.toast.shareLinkCopiedBody': 'लिंक रखने वाला कोई भी यह सारांश पढ़ सकता है।',
  'meetingDetail.toast.couldNotCreateShareLink': 'साझा लिंक नहीं बना सके',
  'meetingDetail.toast.cleaningStarted': 'सफ़ाई शुरू हुई',
  'meetingDetail.toast.cleaningStartedBody': 'यह पृष्ठभूमि में चलता है; एक मिनट में रीफ़्रेश करें। रिकॉर्ड किया गया ट्रांसक्रिप्ट और सारांश अपरिवर्तित हैं।',
  'meetingDetail.toast.cleaningNotEnabled': 'इस वातावरण में सफ़ाई सक्षम नहीं है',
  'meetingDetail.toast.couldNotStartCleaning': 'सफ़ाई शुरू नहीं कर सके',
  'meetingDetail.toast.meetingDeleted': 'मीटिंग हटाई गई',
  'meetingDetail.toast.meetingDeletedBody': 'ऑडियो, ट्रांसक्रिप्ट और सारांश हटा दिए गए हैं।',
  'meetingDetail.toast.deleteFailed': 'मीटिंग हटाई नहीं जा सकी',
  'meetingDetail.toast.exportFailed': 'मीटिंग रिपोर्ट नहीं बनाई जा सकी',

  /* --- transcript ------------------------------------------------------- */
  'transcript.find': 'ट्रांसक्रिप्ट में खोजें…',
  'transcript.searchAria': 'इस ट्रांसक्रिप्ट में खोजें',
  'transcript.match': '{count} मेल',
  'transcript.matches': '{count} मेल',
  'transcript.participantsCount': '{count} प्रतिभागी',
  'transcript.distinctVoices': '{count} अलग-अलग आवाज़ें',
  'transcript.noCleanedTitle': 'कोई साफ़ किया गया ट्रांसक्रिप्ट नहीं',
  'transcript.noCleanedNoTranscript': 'इस मीटिंग का कोई ट्रांसक्रिप्ट नहीं है जिसे साफ़ किया जाए।',
  'transcript.noCleanedNotRun': 'इस मीटिंग पर ग़लत ट्रांसक्रिप्शन मरम्मत नहीं चली है। इसे अभी चलाने पर मौजूदा ट्रांसक्रिप्ट पढ़ा जाता है और एक साफ़ प्रति लिखी जाती है — रिकॉर्ड किया गया ट्रांसक्रिप्ट, सारांश और कार्य ठीक वैसे ही रहते हैं।',
  'transcript.cleanThisTranscript': 'इस ट्रांसक्रिप्ट को साफ़ करें',
  'transcript.inProgressTitle': 'ट्रांसक्रिप्शन जारी है',
  'transcript.inProgressBody': 'रिकॉर्डिंग को ट्रांसक्राइब और वक्ता-मिलान किया जा रहा है। यह पृष्ठ स्वयं अपडेट होता है।',
  'transcript.noTranscriptTitle': 'कोई ट्रांसक्रिप्ट नहीं',
  'transcript.noTranscriptBody': 'इस मीटिंग का कोई ट्रांसक्रिप्ट नहीं है। या तो यह रिकॉर्ड नहीं हुई, या रिकॉर्डिंग में कोई बातचीत नहीं थी।',
  'transcript.notice.changedNothingPrefix': 'इस ट्रांसक्रिप्ट पर ग़लत ट्रांसक्रिप्शन मरम्मत चली और इसने ',
  'transcript.notice.nothing': 'कुछ नहीं',
  'transcript.notice.lineRepaired': '{count} पंक्ति',
  'transcript.notice.linesRepaired': '{count} पंक्तियाँ',
  'transcript.notice.repairedBy': ' {model} द्वारा मरम्मत की गई। प्रत्येक चिह्नित है, उसके नीचे रिकॉर्ड किए गए शब्दों के साथ।',
  'transcript.notice.repairedPlain': ' मरम्मत की गई। प्रत्येक चिह्नित है, उसके नीचे रिकॉर्ड किए गए शब्दों के साथ।',
  'transcript.notice.summaryNote': ' सारांश और कार्य रिकॉर्ड किए गए ट्रांसक्रिप्ट से बनते हैं, इससे नहीं।',
  'transcript.notice.runAgain': 'फिर से चलाएँ',
  'transcript.line.lowConfidence': 'कम विश्वास',
  'transcript.line.lowConfidenceTip': 'वाणी पहचानकर्ता इस पंक्ति के बारे में अनिश्चित था। इसे ऑडियो से मिलाकर जाँचें।',
  'transcript.line.repaired': 'मरम्मत की गई',
  'transcript.line.originallyTranscribedAs': 'मूल रूप से ट्रांसक्राइब किया गया: “{text}”',
  'transcript.line.playFrom': '{time} से चलाएँ',

  /* --- summary -------------------------------------------------------------- */
  'player.play': 'चलाएँ',
  'player.pause': 'रोकें',
  'player.back': '{seconds} सेकंड पीछे',
  'player.forward': '{seconds} सेकंड आगे',
  'player.mute': 'म्यूट करें',
  'player.unmute': 'अनम्यूट करें',
  'player.scrub': 'रिकॉर्डिंग में आगे-पीछे जाएँ',
  'player.speedTip': 'चलाने की गति',
  'player.unavailable': 'इस मीटिंग के लिए अभी कोई ऑडियो संग्रहीत नहीं है।',
  'player.notConfigured': 'इस परिनियोजन पर रिकॉर्डिंग चलाना सक्षम नहीं है।',
  'player.mediaError': 'रिकॉर्डिंग लोड होना रुक गई। इसका लिंक समाप्त हो सकता है।',
  'player.forbidden': 'आपकी भूमिका में रिकॉर्डिंग चलाने की अनुमति शामिल नहीं है। भूमिकाएँ प्रबंधित करने वाला कोई व्यक्ति \u2014 डिफ़ॉल्ट रूप से वर्कस्पेस का स्वामी \u2014 इसे सेटिंग्स \u2192 भूमिकाएँ में दे सकता है।',
  'player.failed': 'रिकॉर्डिंग लोड नहीं हो सकी।',
  'player.retry': 'फिर कोशिश करें',

  'summary.beingGeneratedTitle': 'सारांश तैयार हो रहा है',
  'summary.beingGeneratedBody': 'निर्णय, कार्य और विवरण ट्रांसक्रिप्शन पूरा होने के बाद तैयार होते हैं।',
  'summary.noSummaryTitle': 'अभी कोई सारांश नहीं',
  'summary.noSummaryBody': 'इस मीटिंग का ट्रांसक्रिप्ट होने के बाद सारांश तैयार होता है।',
  'summary.notInEnglishTitle': 'इस सारांश का कुछ हिस्सा अंग्रेज़ी में नहीं है',
  'summary.fieldsUntranslated': '{count} फ़ील्ड में अब भी अअनुवादित पाठ है। ट्रांसक्रिप्ट अप्रभावित है।',
  'summary.discussion': 'चर्चा',
  'summary.generatedPrompt': '{when} को तैयार किया गया · प्रॉम्प्ट {version}',
  'summary.copy': 'कॉपी',
  'summary.keyPoints': 'मुख्य बिंदु',
  'summary.keyPointsDerivedDesc': 'ऊपर दी गई चर्चा से लिए गए — इस मीटिंग का सारांश मुख्य बिंदु बनने से पहले तैयार हुआ था।',
  'summary.keyPointsDerivedBadge': 'चर्चा से',
  'summary.noKeyPointsTitle': 'कोई मुख्य बिंदु नहीं',
  'summary.noKeyPointsBody': 'इस मीटिंग के लिए सारांश में कोई मुख्य बिंदु नहीं बने, और लेने के लिए कोई चर्चा भी नहीं थी।',
  'summary.decisions': 'निर्णय',
  'summary.decisionsDesc': 'प्रत्येक उन शब्दों को रखता है जिनमें वह लिया गया था।',
  'summary.deciderNotIdentified': 'निर्णयकर्ता की पहचान नहीं हुई',
  'summary.hideQuote': 'उद्धरण छिपाएँ',
  'summary.showQuote': 'उद्धरण दिखाएँ',
  'summary.quoteTip': 'शब्दशः और कभी अनुवादित नहीं — यह प्रमाण है, व्याख्या नहीं।',
  'summary.openQuestions': 'खुले प्रश्न',
  'summary.openQuestionsDesc': 'इस मीटिंग में उठाए गए और हल न हुए।',
  'summary.actionItems': 'कार्य सूची',
  'summary.unassigned': 'असाइन नहीं',
  'summary.inferredSuffix': ' (अनुमानित)',
  'summary.copied': 'सारांश कॉपी किया गया',
  'summary.copy.keyPointsHeading': 'मुख्य बिंदु:',
  'summary.copy.keyPointsDerivedHeading': 'मुख्य बिंदु (चर्चा से लिए गए):',
  'summary.copy.decisionsHeading': 'निर्णय:',
  'summary.copy.actionItemsHeading': 'कार्य सूची:',

  /* --- attribution notice --------------------------------------------------- */
  'attribution.suspectTitle': 'इस पूरी मीटिंग में वक्ताओं के नाम ग़लत हो सकते हैं',
  'attribution.suspectBody': 'कैप्शन और ऑडियो घड़ियाँ असहमत हैं, इसलिए इस ट्रांसक्रिप्ट पर हर नाम एक अनुमान है — इसके कार्यों के स्वामी भी। यहाँ निर्दिष्ट किसी भी चीज़ पर कार्य करने से पहले ऑडियो सुनें, और यह सारांश कार्यस्थल के बाहर साझा न करें।',
  'attribution.noNamesTitle': 'कोई वक्ता नाम कैप्चर नहीं हुआ',
  'attribution.noNamesBody': 'इस मीटिंग में कभी कैप्शन नहीं आए, इसलिए आवाज़ों को Speaker 1, Speaker 2 आदि लेबल किया गया है। शब्द विश्वसनीय हैं; किसने कहा यह दर्ज नहीं है।',
  'attribution.attributed': '{percent} निर्दिष्ट',
  'attribution.notAttributedTip': '{percent} बातचीत किसी नामित व्यक्ति को निर्दिष्ट नहीं है।',

  /* --- speaker chip ------------------------------------------------------- */
  'speaker.unnamedWithLabel': 'बिना नाम ({label})',
  'speaker.unnamed': 'बिना नाम वक्ता',

  /* --- action items --------------------------------------------------------- */
  'actions.title': 'कार्य सूची',
  'actions.description': 'मीटिंग से निकाले गए वचन, उन्हें करने वाले व्यक्ति और उस क्षण के साथ। किसी एक कंपनी का बकाया देखने के लिए ग्राहक से फ़िल्टर करें।',
  'actions.status': 'स्थिति',
  'actions.owner': 'स्वामी',
  'actions.status.all': 'सभी',
  'actions.status.open': 'खुला',
  'actions.status.inProgress': 'प्रगति में',
  'actions.status.done': 'पूर्ण',
  'actions.owner.all': 'सभी',
  'actions.owner.me': 'मेरे',
  'actions.owner.unassigned': 'असाइन नहीं',
  'actions.noItemsTitle': 'कोई कार्य नहीं',
  'actions.noItemsBody': 'मीटिंग में किए गए वचन यहाँ उन्हें करने वाले व्यक्ति के साथ दिखते हैं। यदि सारांश छूट गया तो आप हाथ से एक जोड़ सकते हैं।',
  'actions.reopen': 'फिर से खोलें: {text}',
  'actions.markDone': 'पूर्ण चिह्नित करें: {text}',
  'actions.edit': 'संपादित करें: {text}',
  'actions.unassignedNobodyTip': 'इसे किसी ने नहीं उठाया। मॉडल ने या तो किसी का नाम नहीं लिया, या ऐसे व्यक्ति का नाम लिया जिसे हम किसी से मिला नहीं सके।',
  'actions.more': 'और…',
  'actions.createJira': 'Jira इश्यू बनाएँ',
  'actions.postSlack': 'Slack पर पोस्ट करें',
  'actions.addNotion': 'Notion में जोड़ें',
  'actions.moveBackToOpen': 'वापस खुला पर ले जाएँ',
  'actions.markInProgress': 'प्रगति में चिह्नित करें',
  'actions.dismissItem': 'आइटम खारिज करें',
  'actions.deleteItem': 'आइटम हटाएँ',
  'actions.addedByHand': 'हाथ से जोड़ा गया',
  'actions.addedByHandTip': 'इसे किसी ने टाइप किया, बजाय इसके कि सारांशकर्ता ने रिकॉर्डिंग से निकाला हो।',
  'actions.inferredTip': 'यह स्वामी नाम की समानता से मिलाया गया, स्पष्ट रूप से नहीं कहा गया। उनका पीछा करने से पहले जाँच लें।',
  'actions.externalTip': 'यहाँ किसी का खाता नहीं है। वे इस मीटिंग का कुछ नहीं देख सकते और हमने उन्हें कुछ नहीं भेजा — उन तक पहुँचने के लिए आइटम को Jira या Slack पर भेजें।',
  'actions.editorText': 'कार्य पाठ',
  'actions.editorHint': 'सहेजने के लिए Enter, रद्द करने के लिए Escape',
  'actions.overduePrefix': 'अतिदेय · ',
  'actions.duePrefix': 'नियत ',
  'actions.setDueDate': 'नियत तिथि सेट करें',
  'actions.dueDate': 'नियत तिथि',
  'actions.clearDueDate': 'नियत तिथि हटाएँ',
  'actions.toast.sentToJira': 'Jira को भेजा गया',
  'actions.toast.postedToSlack': 'Slack पर पोस्ट किया गया',
  'actions.toast.notionNotConnected': 'Notion कनेक्ट नहीं है',
  'actions.toast.itemChangedTitle': 'किसी और ने इसे पहले बदल दिया',
  'actions.toast.itemChangedBody': 'आपका संपादन सहेजा नहीं गया इसलिए उनका अधिलेखित नहीं कर सका। रीफ़्रेश करके फिर प्रयास करें।',
  'actions.toast.notFoundTitle': 'हम उस व्यक्ति को नहीं ढूँढ सके',
  'actions.toast.notFoundBody': 'वे इस मीटिंग या इस कार्यस्थल में नहीं हैं। इसे इसके बाहर किसी को देने के लिए ईमेल पते से असाइन करें।',
  'actions.toast.updateFail': 'उस कार्य को अपडेट नहीं कर सके',
  'actions.toast.saveFail': 'वह परिवर्तन सहेज नहीं सके',
  'actions.toast.addFail': 'वह कार्य जोड़ नहीं सके',
  'actions.toast.deleteFail': 'वह कार्य हटा नहीं सके',
  'actions.toast.changeNotSaved': 'आपका परिवर्तन सहेजा नहीं गया। फिर से प्रयास करें।',
  'actions.toast.addedTitle': 'कार्य जोड़ा गया',
  'actions.toast.addedAssigned': '{name} को असाइन किया गया।',
  'actions.toast.addedUnassigned': 'अभी इसे किसी को असाइन नहीं किया गया।',
  'actions.deepLinkTitle': 'सूचना से एक आइटम दिखाया जा रहा है',
  'actions.deepLinkClear': 'सभी कार्य आइटम देखें',

  /* --- add action item --------------------------------------------------- */
  'addAction.add': 'कार्य जोड़ें',
  'addAction.placeholder': 'क्या होना चाहिए?',
  'addAction.text': 'कार्य पाठ',
  'addAction.due': 'नियत',
  'addAction.selectedPerson': 'चयनित व्यक्ति',

  /* --- assignee picker --------------------------------------------------- */
  'assignee.assign': 'असाइन करें',
  'assignee.dialogLabel': 'इस कार्य को असाइन करें',
  'assignee.searchPlaceholder': 'लोग खोजें, या ईमेल टाइप करें',
  'assignee.searchAria': 'लोग खोजें, या ईमेल पता टाइप करें',
  'assignee.loadFail': 'सूची लोड नहीं कर सके। आप अब भी ईमेल पते से असाइन कर सकते हैं।',
  'assignee.group.inThisMeeting': 'इस मीटिंग में',
  'assignee.group.inTheWorkspace': 'कार्यस्थल में',
  'assignee.group.outside': 'इस कार्यस्थल के बाहर',
  'assignee.nobodyMatches': '“{query}” से यहाँ कोई मेल नहीं खाता। इस कार्यस्थल के बाहर किसी को असाइन करने के लिए उनका पूरा ईमेल पता टाइप करें।',
  'assignee.outsideNoAccess': 'उन्हें इस मीटिंग की कोई पहुँच और हमसे कोई ईमेल नहीं मिलती — आइटम बस उनका नाम रखता है।',
  'assignee.leaveUnassigned': 'असाइन न रखें',
  'assignee.assignOutside': 'इस कार्यस्थल के बाहर किसी को असाइन करें',
  'assignee.typeInBox': 'ऊपर बॉक्स में उनका ईमेल पता टाइप करें। उन्हें कुछ नहीं भेजा जाता, और इस मीटिंग की कोई पहुँच नहीं मिलती।',
  'assignee.notInThisMeeting': 'इस मीटिंग में नहीं',
  'assignee.unassigned': 'असाइन नहीं',
  'assignee.inferred': 'अनुमानित',
  'assignee.external': 'बाहरी',
  'assignee.memberNotInMeetingTitle': 'वे इस मीटिंग के आमंत्रण में नहीं थे, इसलिए उन्होंने यह तय होते नहीं सुना।',

  /* --- ask ------------------------------------------------------------------- */
  'ask.title': 'सभा से पूछें',
  'ask.description': 'आपकी मीटिंग से उत्तर, उन क्षणों के साथ जहाँ से वे आए।',
  'ask.emptyTitle': 'जो कुछ कहा गया उसके बारे में पूछें',
  'ask.emptyBody': 'उत्तर केवल उन मीटिंग से लिए जाते हैं जिन तक आपकी पहुँच है, और हर दावे का उद्धरण दिया जाता है।',
  'ask.sources': 'स्रोत',
  'ask.reading': 'आपकी मीटिंग पढ़ी जा रही हैं…',
  'ask.inputPlaceholder': 'किसी निर्णय, स्वामी या समय-सीमा के बारे में पूछें…',
  'ask.inputAria': 'आपका प्रश्न',
  'ask.send': 'पूछें',
  'ask.disclaimer': 'उत्तर ग़लत हो सकते हैं। किसी पर कार्य करने से पहले उद्धृत क्षण जाँचें।',
  'ask.suggestion.1': 'FMS कटओवर के बारे में हमने क्या तय किया?',
  'ask.suggestion.2': 'Northwind सौदे को क्या रोक रहा है?',
  'ask.suggestion.3': 'इस सप्ताह Kareena ने जिन बातों का वचन दिया उन सबका सारांश दें',
  'ask.suggestion.4': 'कौन-से कार्य अतिदेय और बिना स्वामी के हैं?',
  'ask.toast.failTitle': 'उसका उत्तर नहीं दे सके',
  'ask.toast.failBody': 'अनुरोध विफल रहा। एक क्षण में फिर से प्रयास करें।',

  /* --- search -------------------------------------------------------------- */
  'search.title': 'खोज',
  'search.description': 'हर उस मीटिंग में कही गई हर बात जिसमें रिकॉर्डर शामिल हुआ।',
  'search.inputPlaceholder': 'ट्रांसक्रिप्ट, शीर्षक और कार्य खोजें…',
  'search.tab.transcript': 'ट्रांसक्रिप्ट',
  'search.tab.meetings': 'मीटिंग',
  'search.tab.actions': 'कार्य सूची',
  'search.idleTitle': 'हर मीटिंग में खोजें',
  'search.idleBody': 'कोई प्रोजेक्ट नाम, कोई निर्णय जो आपको आधा याद हो, या किसी व्यक्ति का नाम आज़माएँ। कम से कम दो अक्षर।',
  'search.noSegmentsTitle': 'उसके बारे में कुछ नहीं कहा गया',
  'search.noSegmentsBody': 'किसी भी ट्रांसक्रिप्ट पंक्ति में यह शब्द नहीं है। कोई छोटा वाक्यांश आज़माएँ — खोज शाब्दिक है, अर्थपूर्ण नहीं। अस्पष्ट प्रश्नों को सभा से पूछें संभालता है।',
  'search.noMeetings': 'कोई मीटिंग शीर्षक मेल नहीं खाता',
  'search.noActions': 'कोई कार्य मेल नहीं खाता',

  /* --- analytics --------------------------------------------------------- */
  'analytics.title': 'विश्लेषण',
  'analytics.description': 'आपका कार्यस्थल कैसे मिलता है, और उसका कितना हिस्सा सभा ने कैप्चर किया।',
  'analytics.dateRange': 'तिथि सीमा',
  'analytics.range.7d': '7 दिन',
  'analytics.range.30d': '30 दिन',
  'analytics.range.90d': '90 दिन',
  'analytics.stat.meetings': 'मीटिंग',
  'analytics.stat.hoursCaptured': 'कैप्चर किए गए घंटे',
  'analytics.stat.actionItems': 'कार्य सूची',
  'analytics.stat.avgLength': 'औसत अवधि',
  'analytics.completed': '{percent} पूर्ण',
  'analytics.recordingVolume': 'रिकॉर्डिंग मात्रा',
  'analytics.recordingVolumeDesc': 'प्रति दिन कैप्चर किए गए मिनट।',
  'analytics.talkTime': 'बोलने का समय',
  'analytics.talkTimeDesc': 'प्रति व्यक्ति निर्दिष्ट बातचीत। अनिर्दिष्ट बातचीत को वितरित करने के बजाय अपनी अलग पंक्ति में दिखाया जाता है।',
  'analytics.topics': 'विषय',
  'analytics.topicsDesc': 'सबसे अधिक चर्चित, उल्लेख के अनुसार।',
  'analytics.languageMix': 'भाषा मिश्रण',
  'analytics.languageMixDesc': 'सभी ट्रांसक्रिप्ट में प्रति खंड पहचाना गया।',
  'chart.talkTime': 'बोलने का समय',
  'chart.minutes': '{value} मिनट',
  'chart.recorded': 'रिकॉर्ड किया गया',

  /* --- integrations --------------------------------------------------------- */
  'integrations.title': 'एकीकरण',
  'integrations.description': 'मीटिंग कहाँ से आती हैं, और उनका परिणाम कहाँ जाता है।',
  'integrations.category.all': 'सभी',
  'integrations.category.calendar': 'कैलेंडर और पहचान',
  'integrations.category.conferencing': 'कॉन्फ़्रेंसिंग',
  'integrations.category.productivity': 'उत्पादकता',
  'integrations.category.crm': 'CRM',
  'integrations.category.storage': 'भंडारण और निर्यात',
  'integrations.category.chat': 'चैट',
  'integrations.status.connected': 'कनेक्ट है',
  'integrations.status.disconnected': 'कनेक्ट नहीं है',
  'integrations.status.error': 'ध्यान चाहिए',
  'integrations.status.pending': 'अनुमोदन की प्रतीक्षा',
  'integrations.needAttention': '{count} कनेक्शन को ध्यान चाहिए',
  'integrations.needAttentionPlural': '{count} कनेक्शनों को ध्यान चाहिए',
  'integrations.connectedBy': '{who} द्वारा कनेक्ट किया गया · {when}',
  'integrations.toast.connected': 'कनेक्ट किया गया',
  'integrations.toast.disconnected': 'डिस्कनेक्ट किया गया',
  'integrations.toast.failTitle': 'यह काम नहीं आया',
  'integrations.toast.failBody': 'प्रदाता ने अनुरोध अस्वीकार कर दिया।',

  /* --- settings: languages pane ----------------------------------------- */
  'langSettings.transcriptionTitle': 'ट्रांसक्रिप्शन भाषाएँ',
  'langSettings.transcriptionDesc': 'नई रिकॉर्डिंग पर लागू। मौजूदा ट्रांसक्रिप्ट फिर से प्रोसेस नहीं होते।',
  'langSettings.detectionMode': 'पहचान मोड',
  'langSettings.mode.identify': 'उम्मीदवार सूची से स्वतः पहचानें (अनुशंसित)',
  'langSettings.mode.pinned': 'हमेशा एक भाषा उपयोग करें',
  'langSettings.candidateLanguages': 'उम्मीदवार भाषाएँ',
  'langSettings.candidateHelp': 'कम उम्मीदवारों का अर्थ है तेज़, अधिक सटीक पहचान। ऐसी भाषाएँ सूचीबद्ध करना जो कोई नहीं बोलता, बोली जाने वालों की सटीकता घटाता है।',
  'langSettings.selectAtLeastOne': 'कम से कम एक भाषा चुनें।',
  'langSettings.language': 'भाषा',
  'langSettings.codeSwitchTitle': 'कोड-स्विच की गई बातचीत बदतर ट्रांसक्राइब होगी',
  'langSettings.codeSwitchBody': 'एक पिन की गई भाषा दूसरी भाषा को ध्वन्यात्मक रूप से ट्रांसक्राइब करती है — हिंदी शब्द अंग्रेज़ी की देवनागरी वर्तनी में आते हैं, या उल्टा। इसे केवल उन मीटिंग के लिए पिन करें जिन्हें आप एकल-भाषा जानते हैं।',
  'langSettings.summaryOutput': 'सारांश आउटपुट',
  'langSettings.alwaysEnglish': 'सारांश हमेशा अंग्रेज़ी में लिखें',
  'langSettings.alwaysEnglishDesc': 'ट्रांसक्रिप्ट बोली गई भाषा में रहते हैं। शब्दशः निर्णय उद्धरण कभी अनुवादित नहीं होते।',
  'langSettings.flagUntranslated': 'ऐसे सारांश चिह्नित करें जिनमें अब भी अअनुवादित पाठ है',
  'langSettings.flagUntranslatedDesc': 'चुपचाप मिश्रित-भाषा आउटपुट भेजने के बजाय एक बैनर जोड़ता है।',

  /* --- settings: notifications pane ------------------------------------------ */
  'notifSettings.recipient.organizer': 'केवल आयोजक (सबसे सुरक्षित)',
  'notifSettings.recipient.internal': 'आपके डोमेन के भीतर के आमंत्रित',
  'notifSettings.recipient.all': 'हर आमंत्रित, बाहरी अतिथियों सहित',
  'notifSettings.recipient.none': 'कोई नहीं — ऐप में सारांश पढ़ें',
  'notifSettings.audience.organizer': 'प्रत्येक रिकॉर्ड की गई मीटिंग का आयोजक',
  'notifSettings.audience.internal': 'हर रिकॉर्ड की गई मीटिंग के लिए, आपके अपने डोमेन के भीतर का हर आमंत्रित',
  'notifSettings.audience.all': 'हर रिकॉर्ड की गई मीटिंग का हर आमंत्रित, आपके डोमेन के बाहर के लोगों सहित',
  'notifSettings.audience.none': 'कोई नहीं',
  'notifSettings.deliveryTitle': 'सारांश डिलीवरी',
  'notifSettings.whoReceives': 'सारांश ईमेल कौन प्राप्त करता है',
  'notifSettings.whoReceivesHint': 'कई उपस्थित लोगों के लिए यह ईमेल पहली बार है जब उन्हें रिकॉर्डिंग का पता चलता है। इसे सोच-समझकर विस्तृत करें।',
  'notifSettings.send': 'भेजें',
  'notifSettings.send.immediate': 'जैसे ही सारांश तैयार हो',
  'notifSettings.send.endOfDay': 'दिन के अंत में एकत्रित',
  'notifSettings.eventsTitle': 'घटनाएँ',
  'notifSettings.eventsDesc': 'सभा किन पर सूचित करता है।',
  'notifSettings.summaryEmail': 'प्रतिभागियों को सारांश ईमेल',
  'notifSettings.summaryEmailDesc': 'मीटिंग तैयार होने पर भेजा जाता है।',
  'notifSettings.failureAlerts': 'रिकॉर्डिंग विफलता अलर्ट',
  'notifSettings.failureAlertsDesc': 'आयोजक और कार्यस्थल व्यवस्थापकों को — हर आमंत्रित को नहीं।',
  'notifSettings.actionAssignment': 'कार्य ईमेल (Sabha Catchup)',
  'notifSettings.actionAssignmentDesc': 'हर व्यक्ति को दिन के अंत में एक ईमेल, उनके खुले कार्यों और नियत तिथियों के साथ — हर असाइनमेंट पर नहीं। बिना स्वामी वाले कार्यों के लिए आयोजक को दिन 1, 3 और 7 पर याद दिलाया जाता है।',
  'notifSettings.weeklyDigest': 'साप्ताहिक कार्यस्थल सारांश',
  'notifSettings.weeklyDigestDesc': 'मात्रा, खुले आइटम और बिना स्वामी वाले वचन।',
  'notifSettings.confirmTitle': 'सारांश ईमेल कौन प्राप्त करता है, विस्तृत करें?',
  'notifSettings.confirmBody': 'सारांश {audience} को ईमेल किए जाएँगे। एक ईमेल वापस नहीं लिया जा सकता, और कई उपस्थित लोगों के लिए यह पहली बार है जब उन्हें पता चलता है कि रिकॉर्डिंग हुई थी।',
  'notifSettings.confirmButton': 'प्राप्तकर्ता विस्तृत करें',

  /* --- settings: recording rules ------------------------------------------- */
  'recording.rulesInOrderTitle': 'नियम क्रम में मूल्यांकित होते हैं, और पहला मेल जीतता है',
  'recording.rulesInOrderBodyPrefix': 'यदि कोई नियम मेल नहीं खाता, तो मीटिंग ',
  'recording.rulesInOrderNot': 'रिकॉर्ड नहीं',
  'recording.rulesInOrderBodySuffix': ' होती। रिकॉर्डर केवल उन्हीं मीटिंग में शामिल होता है जिनमें उसके मेलबॉक्स को आमंत्रित किया गया था, चाहे ये नियम कुछ भी कहें।',
  'recording.rulesTitle': 'रिकॉर्डिंग नियम',
  'recording.addRule': 'नियम जोड़ें',
  'recording.enableRule': 'नियम सक्षम करें: {name}',
  'recording.retention': 'प्रतिधारण',
  'recording.retentionDesc': 'हटाने से पहले कलाकृतियाँ कितने समय तक रखी जाती हैं।',
  'recording.retention.audio': 'ऑडियो',
  'recording.retention.transcript': 'ट्रांसक्रिप्ट',
  'recording.retention.summary': 'सारांश',
  'recording.retention.audioValue': '30 दिन',
  'recording.retention.transcriptValue': '12 महीने',
  'recording.retention.summaryValue': 'अनिश्चित काल',

  /* --- settings: security ----------------------------------------------------- */
  'security.authTitle': 'प्रमाणीकरण',
  'security.authDesc': 'सदस्य कैसे साइन इन करते हैं।',
  'security.requireSso': 'सभी सदस्यों के लिए SSO अनिवार्य करें',
  'security.requireSsoDesc': 'यह चालू होने पर पासवर्ड साइन-इन अक्षम हो जाता है।',
  'security.enforce2fa': 'व्यवस्थापकों के लिए दो-कारक अनिवार्य करें',
  'security.samlUrl': 'SAML मेटाडेटा URL',
  'security.retentionTitle': 'प्रतिधारण',
  'security.retentionDesc': 'हटाने से पहले प्रत्येक कलाकृति कितने समय तक रखी जाती है। योजना-स्तर; बदलने के लिए सहायता से संपर्क करें।',
  'security.retention.audio': 'ऑडियो',
  'security.retention.transcript': 'ट्रांसक्रिप्ट',
  'security.retention.summary': 'सारांश',
  'security.retention.days': '{count} दिन',
  'security.retention.months': '{count} महीने',
  'security.retention.indefinite': 'अनिश्चित काल',
  'security.exportTitle': 'अपना डेटा निर्यात करें',
  'security.exportDesc': 'ऑडियो, ट्रांसक्रिप्ट और सारांश एक ही संग्रह के रूप में, एक समाप्त होने वाले लिंक से दिया जाता है।',
  'security.requestExport': 'निर्यात अनुरोध करें',
  'security.dangerZone': 'ख़तरा क्षेत्र',
  'security.dangerZoneDesc': 'ये क्रियाएँ पूर्ववत नहीं की जा सकतीं।',
  'security.deleteWorkspaceAlertTitle': 'कार्यस्थल हटाने से हर रिकॉर्डिंग हट जाती है',
  'security.deleteWorkspaceAlertBody': 'हर सदस्य के लिए सभी ऑडियो, ट्रांसक्रिप्ट, सारांश और कार्य स्थायी रूप से हटा दिए जाते हैं।',
  'security.deleteWorkspace': 'यह कार्यस्थल हटाएँ',
  'security.ssoConfirmTitle': 'सभी के लिए SSO अनिवार्य करें?',
  'security.ssoConfirmBody': 'पासवर्ड साइन-इन तुरंत अक्षम हो जाता है। यदि आपके पहचान प्रदाता का मैपिंग ग़लत है, तो कोई भी — आप सहित — साइन इन नहीं कर सकता। पहले किसी दूसरे टैब में अपना SSO लॉगिन सत्यापित करें।',
  'security.ssoConfirmButton': 'SSO अनिवार्य करें',
  'security.deleteConfirmTitle': 'यह कार्यस्थल हटाएँ?',
  'security.deleteConfirmBody': 'सभी सदस्यों के लिए हर रिकॉर्डिंग, ट्रांसक्रिप्ट और सारांश स्थायी रूप से हटा दिया जाता है। यह पूर्ववत नहीं किया जा सकता।',
  'security.deleteConfirmButton': 'सब कुछ हटाएँ',
  'security.toast.deletionDisabled': 'इस बिल्ड में कार्यस्थल हटाना अक्षम है',

  /* --- settings: billing ------------------------------------------------------ */
  'billing.col.period': 'अवधि',
  'billing.col.amount': 'राशि',
  'billing.col.status': 'स्थिति',
  'billing.stat.plan': 'योजना',
  'billing.stat.renews': '{date} को नवीनीकरण',
  'billing.stat.seats': 'सीटें',
  'billing.stat.minutesUsed': 'उपयोग किए गए मिनट',
  'billing.stat.ofIncluded': '{count} शामिल में से',
  'billing.usageTitle': 'इस चक्र में उपयोग',
  'billing.usageDesc': 'रिकॉर्डिंग मिनट कैप्चर किए गए ऑडियो पर मापे जाते हैं, निर्धारित मीटिंग अवधि पर नहीं।',
  'billing.changePlan': 'योजना बदलें',
  'billing.minutesUsedLabel': 'उपयोग किए गए मिनट',
  'billing.minutesRemaining': '{count} मिनट शेष। अधिक उपयोग चक्र के अंत में प्रति मिनट बिल किया जाता है।',
  'billing.invoices': 'चालान',

  /* --- settings: members ---------------------------------------------------- */
  'members.col.member': 'सदस्य',
  'members.col.role': 'भूमिका',
  'members.col.status': 'स्थिति',
  'members.col.lastActive': 'अंतिम सक्रिय',
  'members.changeTo': '{role} में बदलें',
  'members.manage': '{name} प्रबंधित करें',
  'members.resendInvitation': 'आमंत्रण फिर से भेजें',
  'members.removeFromWorkspace': 'कार्यस्थल से हटाएँ',
  'members.title': 'सदस्य',
  'members.description': 'इस कार्यस्थल में कौन है, और उनमें से प्रत्येक क्या कर सकता है।',
  'members.invitePeople': 'लोगों को आमंत्रित करें',
  'members.toast.updated': 'सदस्य अपडेट किया गया',
  'members.toast.updateFail': 'उस सदस्य को अपडेट नहीं कर सके',
  'members.toast.invitationResent': 'आमंत्रण फिर से भेजा गया',
  'members.status.active': 'सक्रिय',
  'members.status.invited': 'आमंत्रित',
  'members.status.suspended': 'निलंबित',

  /* --- settings: workspace ------------------------------------------------- */
  'workspace.title': 'कार्यस्थल',
  'workspace.description': 'इस टेनेंट में हर किसी के लिए पहचान और डिफ़ॉल्ट।',
  'workspace.name': 'कार्यस्थल नाम',
  'workspace.nameHint': 'टेनेंट स्विचर और बाहर जाने वाले ईमेल में दिखाई देता है।',
  'workspace.verifiedDomain': 'सत्यापित डोमेन',
  'workspace.verifiedDomainHint': 'इस ईमेल डोमेन वाला कोई भी बिना आमंत्रण के शामिल हो सकता है।',
  'workspace.dataResidency': 'डेटा निवास',
  'workspace.region.ap': 'भारत (मुंबई)',
  'workspace.region.eu': 'आयरलैंड',
  'workspace.region.us': 'संयुक्त राज्य (एन. वर्जीनिया)',
  'workspace.dataResidencyHint': 'निर्माण पर तय। कार्यस्थल माइग्रेट करने के लिए सहायता से संपर्क करें।',
  'workspace.recorderName': 'रिकॉर्डर प्रदर्शन नाम',
  'workspace.recorderNameHint': 'मीटिंग में प्रतिभागियों को दिखाया जाता है।',
  'workspace.disclosureTitle': 'प्रकटीकरण',
  'workspace.disclosureDesc': 'प्रतिभागियों को क्या बताया जाता है। कैलेंडर आमंत्रण प्राथमिक सहमति रिकॉर्ड है।',
  'workspace.announceRecording': 'शामिल होने पर मीटिंग चैट में रिकॉर्डिंग की घोषणा करें',
  'workspace.announceRecordingDesc': 'रिकॉर्डर के प्रवेश पर एक पंक्ति का नोटिस पोस्ट करता है।',
  'workspace.requireExternalTold': 'रिकॉर्डिंग शुरू होने से पहले हर बाहरी प्रतिभागी को बताना अनिवार्य करें',
  'workspace.requireExternalToldDesc': 'यदि कोई आमंत्रित आपके डोमेन के बाहर है और उसने नोटिस स्वीकार नहीं किया है तो मीटिंग छोड़ देता है।',

  /* --- patrons ----------------------------------------------------------------- */
  'patrons.aboutTitle': 'ग्राहक किसलिए है',
  'patrons.aboutBodyPrefix': 'जिन कंपनियों के लिए आप काम करते हैं उन्हें टैग करें, और हर मीटिंग तथा कार्य को उनके अनुसार फ़िल्टर किया जा सकता है। एक मीटिंग ',
  'patrons.aboutAutomatically': 'स्वचालित रूप से',
  'patrons.aboutBodyMid': ' लिंक हो जाती है जब ग्राहक के किसी डोमेन से कोई आमंत्रण में था — उन मीटिंग सहित जो आपने ग्राहक जोड़ने से पहले रिकॉर्ड कीं। किसी ग्राहक के ',
  'patrons.aboutAbout': 'बारे में',
  'patrons.aboutBodySuffix': ' एक आंतरिक चर्चा में कोई उपस्थित नहीं होता, इसलिए उन्हें मीटिंग पर ही टैग करें।',
  'patrons.alreadyMeetWith': 'जिन कंपनियों से आप पहले से मिलते हैं',
  'patrons.alreadyMeetWithDesc': 'आपके आमंत्रणों से बाहरी डोमेन जिन्हें अभी कोई ग्राहक कवर नहीं करता। एक जोड़ने से उसकी पिछली मीटिंग भी दावा हो जाती हैं।',
  'patrons.oneMeeting': '{count} मीटिंग',
  'patrons.manyMeetings': '{count} मीटिंग',
  'patrons.lastMet': ' · अंतिम {date}',
  'patrons.example': ' · जैसे {name}',
  'patrons.addAsPatron': 'ग्राहक के रूप में जोड़ें',
  'patrons.addAPatron': 'एक ग्राहक जोड़ें',
  'patrons.loading': 'ग्राहक लोड हो रहे हैं…',
  'patrons.noPatronsTitle': 'अभी कोई ग्राहक नहीं',
  'patrons.noPatronsBody': 'जिन कंपनियों के लिए आप काम करते हैं उन्हें जोड़ें, फिर मीटिंग और कार्य को उनके अनुसार फ़िल्टर करें।',
  'patrons.openCount': ' · {count} खुले',
  'patrons.restoreTip': 'इस ग्राहक को फ़िल्टर सूची में वापस लाएँ।',
  'patrons.archiveTip': 'इसे फ़िल्टर सूची से छिपाएँ। इसकी मीटिंग और कार्य रखे जाते हैं — किसी ग्राहक संबंध का समाप्त होना काम को पूर्ववत नहीं करता।',
  'patrons.renameTip': 'इस ग्राहक का नाम बदलें',
  'patrons.renameAria': '{name} का नाम बदलें',
  'patrons.removeDomain': '{domain} हटाएँ',
  'patrons.addDomainPlaceholder': 'एक डोमेन जोड़ें, जैसे northwind.example',
  'patrons.addDomainAria': '{name} में एक डोमेन जोड़ें',
  'patrons.noDomainsYet': 'अभी कोई डोमेन नहीं, इसलिए कुछ भी स्वचालित रूप से लिंक नहीं होता — इस ग्राहक को मीटिंग से हाथ से जोड़ें, या वह डोमेन जोड़ें जहाँ से उनके लोग ईमेल करते हैं।',
  'patrons.toast.added': 'ग्राहक जोड़ा गया',
  'patrons.toast.addFail': 'वह ग्राहक जोड़ नहीं सके',
  'patrons.toast.saveFail': 'वह ग्राहक सहेज नहीं सके',
  'patrons.toast.nameTaken': 'इस नाम का एक ग्राहक इस वर्कस्पेस में पहले से मौजूद है।',
  'patrons.toast.domainAddFail': 'वह डोमेन जोड़ नहीं सके',
  'patrons.toast.domainAdded': 'डोमेन जोड़ा गया',
  'patrons.toast.domainAddedBodyOne': '{name} अब {count} मीटिंग कवर करता है, पिछली सहित।',
  'patrons.toast.domainAddedBodyMany': '{name} अब {count} मीटिंग कवर करता है, पिछली सहित।',
  'patrons.toast.domainRemoveFail': 'वह डोमेन हटा नहीं सके',
  'patrons.toast.domainRemoved': 'डोमेन हटाया गया',
  'patrons.toast.domainRemovedBody': 'इसने जो मीटिंग कवर कीं वे अब इस ग्राहक से लिंक नहीं हैं।',
  'patrons.toast.meetingPatronsFail': 'इस मीटिंग के ग्राहक अपडेट नहीं कर सके',

  /* --- patron filter ---------------------------------------------------------- */
  'patronFilter.heading': 'ग्राहक',
  'patronFilter.any': 'कोई भी ग्राहक',
  'patronFilter.none': 'कोई ग्राहक नहीं',
  'patronFilter.fallback': 'ग्राहक',
  'patronFilter.emptyHint': 'अभी कोई ग्राहक नहीं — उन्हें सेटिंग्स में जोड़ें',

  /* --- meeting patrons ------------------------------------------------------ */
  'meetingPatrons.none': 'कोई ग्राहक नहीं',
  'meetingPatrons.fromInviteTip': 'स्वचालित रूप से लिंक किया गया क्योंकि {name} से कोई आमंत्रण में था। इसे अनलिंक करने के लिए, सेटिंग्स में ग्राहक से उनका डोमेन हटाएँ।',
  'meetingPatrons.manualTip': 'हाथ से जोड़ा गया। {name} का इस आमंत्रण में कोई नहीं था।',
  'meetingPatrons.fromInvite': 'आमंत्रण से',
  'meetingPatrons.remove': '{name} हटाएँ',
  'meetingPatrons.addHeading': 'एक ग्राहक जोड़ें',
  'meetingPatrons.addTrigger': 'ग्राहक',
  'meetingPatrons.emptyHint': 'अभी कोई ग्राहक नहीं — उन्हें सेटिंग्स में जोड़ें',
  'meetingPatrons.allAttached': 'हर ग्राहक पहले से इस मीटिंग पर है',

  /* --- action item patron -------------------------------------------------- */
  'actionPatron.none': 'कोई ग्राहक नहीं',
  'actionPatron.overriddenTip': 'इस आइटम पर सेट, इसलिए यह मीटिंग का अनुसरण नहीं करता।',
  'actionPatron.inheritedManyTip': 'मीटिंग से विरासत में, जो {count} ग्राहकों को कवर करती है — इसलिए यह आइटम उन सभी के विरुद्ध गिना जाता है। इसे सटीक बनाने के लिए एक सेट करें।',
  'actionPatron.inheritedTip': 'मीटिंग से विरासत में।',
  'actionPatron.followMeeting': 'मीटिंग का अनुसरण करें',

  /* --- roles --------------------------------------------------------------- */
  'roles.title': 'भूमिकाएँ',
  'roles.description': 'इस कार्यस्थल में प्रत्येक भूमिका क्या देख और कर सकती है।',
  'roles.newRole': 'नई भूमिका',
  'roles.readOnlyTitle': 'केवल पढ़ने योग्य',
  'roles.readOnlyBody': 'आप देख सकते हैं कि यह कार्यस्थल कैसे सेट है। किसी भूमिका को बदलने के लिए “भूमिकाएँ प्रबंधित करें” अनुमति चाहिए।',
  'roles.col.role': 'भूमिका',
  'roles.col.meetingAccess': 'मीटिंग पहुँच',
  'roles.col.permissions': 'अनुमतियाँ',
  'roles.col.members': 'सदस्य',
  'roles.protectedTip': 'चाहे उनके पास कोई भी अनुमति हो, केवल वही व्यक्ति इसे बदल सकता है जो यह भूमिका रखता है।',
  'roles.protected': 'सुरक्षित',
  'roles.access.every': 'हर मीटिंग',
  'roles.access.own': 'अपनी मीटिंग',
  'roles.access.none': 'कोई मीटिंग नहीं',
  'roles.manage': '{name} प्रबंधित करें',
  'roles.editPermissions': 'अनुमतियाँ संपादित करें',
  'roles.deleteRole': 'भूमिका हटाएँ',
  'roles.editor.newRole': 'नई भूमिका',
  'roles.editor.editRole': '{name} संपादित करें',
  'roles.editor.role': 'भूमिका',
  'roles.editor.permissionCount': '{count} अनुमति',
  'roles.editor.permissionCountPlural': '{count} अनुमतियाँ',
  'roles.editor.createRole': 'भूमिका बनाएँ',
  'roles.editor.saveRole': 'भूमिका सहेजें',
  'roles.editor.name': 'नाम',
  'roles.editor.namePlaceholder': 'संचालन',
  'roles.editor.description': 'विवरण',
  'roles.editor.descriptionPlaceholder': 'यह भूमिका किसलिए है। जो इसे असाइन करता है उसे दिखाया जाता है।',
  'roles.editor.builtInTitle': 'अंतर्निहित भूमिका',
  'roles.editor.builtInBodyPrefix': 'यहाँ हर अनुमति आपकी बदलने योग्य है। भूमिका स्वयं हटाई नहीं जा सकती, और इसकी आंतरिक कुंजी ',
  'roles.editor.builtInBodySuffix': ' रहती है ताकि आमंत्रण और प्रावधान काम करते रहें।',
  'roles.editor.ownMeetingsOnlyTitle': 'केवल अपनी मीटिंग',
  'roles.editor.ownMeetingsOnlyBody': 'धारक केवल उन्हीं मीटिंग को देखेंगे जिनमें उन्हें आमंत्रित किया गया था। यह उन कर्मचारियों के लिए सेटिंग है जो कार्यस्थल का प्रशासन करते हैं बिना उसकी बातचीत पढ़े।',
  'roles.editor.noAccessTitle': 'बिल्कुल कोई मीटिंग पहुँच नहीं',
  'roles.editor.noAccessBody': 'धारक कोई मीटिंग नहीं देखेंगे — वे भी नहीं जिनमें उन्हें आमंत्रित किया गया था।',
  'roles.editor.youDoNotHold': 'आपके पास यह अनुमति नहीं है',
  'roles.category.meetings': 'मीटिंग और रिकॉर्डिंग',
  'roles.category.actions': 'कार्य सूची',
  'roles.category.insight': 'खोज और विश्लेषण',
  'roles.category.patrons': 'ग्राहक',
  'roles.category.workspace': 'कार्यस्थल प्रशासन',
  'roles.category.billing': 'बिलिंग',
  'roles.toast.createFail': 'वह भूमिका नहीं बना सके',
  'roles.toast.created': 'भूमिका बनाई गई',
  'roles.toast.createdBody': '{name} अब सदस्यों को असाइन की जा सकती है।',
  'roles.toast.saveFail': 'वह भूमिका सहेज नहीं सके',
  'roles.toast.saved': 'भूमिका सहेजी गई',
  'roles.toast.savedBody': '{count} {verb} {name}।',
  'roles.toast.savedVerbOne': 'सदस्य रखता है',
  'roles.toast.savedVerbMany': 'सदस्य रखते हैं',
  'roles.toast.deleteFail': 'वह भूमिका हटा नहीं सके',
  'roles.toast.deleted': 'भूमिका हटाई गई',

  /* --- tenants ------------------------------------------------------------- */
  'tenants.title': 'टेनेंट',
  'tenants.description': 'इस परिनियोजन पर हर कार्यस्थल। सामग्री यहाँ कभी नहीं दिखाई जाती।',
  'tenants.createTenant': 'टेनेंट बनाएँ',
  'tenants.count': '{count} टेनेंट',
  'tenants.col.tenant': 'टेनेंट',
  'tenants.col.plan': 'योजना',
  'tenants.col.status': 'स्थिति',
  'tenants.col.seats': 'सीटें',
  'tenants.col.minutes': 'मिनट',
  'tenants.col.region': 'क्षेत्र',
  'tenants.col.storagePrefix': 'भंडारण उपसर्ग',
  'tenants.col.created': 'बनाया गया',
  'tenants.openToDomain': '· डोमेन के लिए खुला',
  'tenants.region.ap': 'भारत',
  'tenants.region.eu': 'आयरलैंड',
  'tenants.region.us': 'यूएस पूर्व',
  'tenants.status.active': 'सक्रिय',
  'tenants.status.onboarding': 'ऑनबोर्डिंग',
  'tenants.status.suspended': 'निलंबित',
  'tenants.manage': '{name} प्रबंधित करें',
  'tenants.openSupportSession': 'सहायता सत्र खोलें',
  'tenants.adjustLimits': 'सीमाएँ समायोजित करें',
  'tenants.reactivate': 'पुनः सक्रिय करें',
  'tenants.suspendTenant': 'टेनेंट निलंबित करें',
  'tenants.toast.supportAudited': 'सहायता सत्रों का ऑडिट किया जाता है',
  'tenants.toast.notBuilt': 'अभी नहीं बनाया गया',
  'tenants.toast.secondApprover': 'एक दूसरे अनुमोदक की आवश्यकता है',

  /* --- create tenant modal ---------------------------------------------------- */
  'createTenant.title': 'टेनेंट बनाएँ',
  'createTenant.subtitle': 'कार्यस्थल प्रावधान करता है और उसके स्वामी को आमंत्रित करता है।',
  'createTenant.region.ap': 'भारत (ap-south-1)',
  'createTenant.region.eu': 'आयरलैंड (eu-west-1)',
  'createTenant.region.us': 'यूएस पूर्व (us-east-1)',
  'createTenant.plan.trial': 'ट्रायल',
  'createTenant.plan.team': 'टीम',
  'createTenant.plan.business': 'बिज़नेस',
  'createTenant.plan.enterprise': 'एंटरप्राइज़',
  'createTenant.failedTitle': 'यह काम नहीं आया',
  'createTenant.couldNotCreate': 'कार्यस्थल नहीं बनाया जा सका।',
  'createTenant.workspaceName': 'कार्यस्थल नाम',
  'createTenant.primaryDomain': 'प्राथमिक डोमेन',
  'createTenant.primaryDomainHint': 'भंडारण उपसर्ग निकालने के लिए उपयोग किया जाता है, जो बाद में नहीं बदल सकता।',
  'createTenant.ownerEmail': 'स्वामी ईमेल',
  'createTenant.ownerEmailHint': 'स्वामी के रूप में आमंत्रित। नए कार्यस्थल में प्रवेश का यही एकमात्र रास्ता है।',
  'createTenant.region': 'क्षेत्र',
  'createTenant.regionHint': 'जहाँ ऑडियो रहता है।',
  'createTenant.plan': 'योजना',
  'createTenant.planHint': 'सीट और मिनट सीमाएँ निर्धारित करता है।',
  'createTenant.allowDomainJoin': 'इस डोमेन के किसी भी व्यक्ति को SSO से शामिल होने दें',
  'createTenant.allowDomainJoinDesc': 'उनका पहचान प्रदाता पते को सत्यापित करता है; वे बिना आमंत्रण के सदस्य के रूप में आते हैं। बंद का अर्थ है हर कर्मचारी को एक चाहिए — और उत्पाद अभी उन्हें कुछ ईमेल नहीं करता। सार्वजनिक मेल प्रदाताओं के लिए उपलब्ध नहीं।',
  'createTenant.toast.provisionedTitle': '{name} का प्रावधान हो गया',
  'createTenant.toast.provisionedBody': '{who} अब कार्यस्थल का दावा करने के लिए साइन इन कर सकता है।',
  'createTenant.toast.provisionedOwnerFallback': 'स्वामी',

  /* --- audit log --------------------------------------------------------- */
  'audit.title': 'ऑडिट लॉग',
  'audit.descSelf': 'इस कार्यस्थल में आपकी अपनी क्रियाएँ।',
  'audit.descWorkspace': 'इस कार्यस्थल में की गई हर विशेषाधिकार प्राप्त क्रिया।',
  'audit.searchPlaceholder': 'कर्ता, क्रिया या लक्ष्य…',
  'audit.col.when': 'कब',
  'audit.col.actor': 'कर्ता',
  'audit.col.action': 'क्रिया',
  'audit.col.target': 'लक्ष्य',
  'audit.col.detail': 'विवरण',
  'audit.col.ip': 'IP',
  'audit.filter.whose': 'किसका',
  'audit.filter.everyone': 'सभी',
  'audit.filter.area': 'क्षेत्र',
  'audit.filter.allAreas': 'सभी क्षेत्र',
  'audit.filter.action': 'क्रिया',
  'audit.filter.allActions': 'सभी क्रियाएँ',
  'audit.filter.from': 'से',
  'audit.filter.to': 'तक',
  'audit.actorEvents': '{name} ({count})',
  'audit.selfTitle': 'आप अपनी क्रियाएँ देख रहे हैं',
  'audit.selfBodyPrefix': 'दूसरों का इतिहास पढ़ने के लिए ',
  'audit.selfBodySuffix': ' अनुमति चाहिए — एक कार्यस्थल व्यवस्थापक या स्वामी इसे किसी भूमिका पर दे सकता है।',
  'audit.empty.filtered': 'इन फ़िल्टरों से कोई घटना मेल नहीं खाती।',
  'audit.empty.self': 'आपने इस कार्यस्थल में अभी तक कोई दर्ज क्रिया नहीं की है।',
  'audit.empty.workspace': 'इस कार्यस्थल में अभी तक कुछ भी दर्ज नहीं हुआ है।',
  'audit.wasNamed': '“{name}” था',

  /* --- auth -------------------------------------------------------------- */
  'login.title': 'सभा में साइन इन करें',
  'login.subtitle': 'उस पहचान प्रदाता का उपयोग करें जिसके साथ आपका कार्यस्थल सेट है।',
  'login.ssoErrorTitle': 'हम आपको साइन इन नहीं कर सके',
  'login.ssoError.directoryNotConfigured': 'उस ईमेल डोमेन के लिए सिंगल साइन-ऑन अभी सेट नहीं है। पता जाँचें, या जिसने आपका कार्यस्थल सेट किया उससे पूछें।',
  'login.ssoError.generic': 'आपका पहचान प्रदाता साइन-इन पूरा नहीं कर सका। फिर से प्रयास करें, और यदि यह बार-बार होता है तो अपने व्यवस्थापक को बताएँ।',
  'login.workEmail': 'कार्य ईमेल',
  'login.workEmailHint': 'हम इसका उपयोग आपके संगठन के पहचान प्रदाता को खोजने के लिए करते हैं।',
  'login.continueMicrosoft': 'Microsoft के साथ जारी रखें',
  'login.continueGoogle': 'Google के साथ जारी रखें',
  'login.password': 'पासवर्ड',
  'login.passwordCheck': 'अपना ईमेल और पासवर्ड जाँचें और फिर से प्रयास करें।',
  'login.genericError': 'कुछ गड़बड़ हो गई। एक क्षण में फिर से प्रयास करें।',
  'login.signInWithPassword': 'पासवर्ड से साइन इन करें',
  'onboarding.title': 'सभा सेट करें',
  'onboarding.subtitle': 'चार चरण, लगभग तीन मिनट। आप छोड़कर वापस आ सकते हैं।',
  'onboarding.step.connect': 'एक कैलेंडर कनेक्ट करें',
  'onboarding.step.connectDesc': 'Microsoft 365 या Google Workspace',
  'onboarding.step.invite': 'रिकॉर्डर को आमंत्रित करें',
  'onboarding.step.inviteDesc': 'बॉट को किसी मीटिंग में जोड़ें',
  'onboarding.step.rules': 'चुनें क्या रिकॉर्ड करना है',
  'onboarding.step.rulesDesc': 'अपना पहला नियम सेट करें',
  'onboarding.step.done': 'हो गया',
  'onboarding.step.doneDesc': 'अपने पहले सारांश की प्रतीक्षा करें',
  'onboarding.connectTitle': 'एक कैलेंडर कनेक्ट करें',
  'onboarding.connectDesc': 'सभा केवल रिकॉर्डर मेलबॉक्स का अपना कैलेंडर पढ़ता है — किसी सदस्य का कभी नहीं।',
  'onboarding.connectM365': 'Microsoft 365 कनेक्ट करें',
  'onboarding.connectGoogle': 'Google Workspace कनेक्ट करें',
  'onboarding.connectNote': 'एक व्यवस्थापक को कनेक्शन एक बार अनुमोदित करना होगा। जब तक आप रिकॉर्डर को किसी मीटिंग में आमंत्रित नहीं करते, कुछ भी रिकॉर्ड नहीं होता।',
  'onboarding.inviteTitle': 'रिकॉर्डर को किसी मीटिंग में आमंत्रित करें',
  'onboarding.inviteDesc': 'सहमति इसी तरह दर्ज की जाती है।',
  'onboarding.copy': 'कॉपी',
  'onboarding.addAsAttendeeTitle': 'इसे एक सामान्य उपस्थित के रूप में जोड़ें',
  'onboarding.addAsAttendeeBody': 'रिकॉर्डर शुरू होने के समय से लगभग दो मिनट पहले शामिल होता है। हर आमंत्रित उसे अतिथि सूची में देख सकता है, जो आमंत्रण को सहमति रिकॉर्ड बनाता है।',
  'onboarding.iHaveInvited': 'मैंने इसे आमंत्रित कर दिया',
  'onboarding.chooseTitle': 'चुनें क्या रिकॉर्ड करना है',
  'onboarding.chooseDesc': 'आप इसे कभी भी बदल सकते हैं।',
  'onboarding.choice.explicit': 'केवल वे मीटिंग जिन्हें मैं स्पष्ट रूप से आमंत्रित करता हूँ',
  'onboarding.choice.explicitDesc': 'और कुछ भी कभी शामिल नहीं होता। सबसे सुरक्षित।',
  'onboarding.choice.external': 'सभी बाहरी मीटिंग',
  'onboarding.choice.externalDesc': 'ग्राहक और विक्रेता कॉल, आंतरिक 1:1 छोड़कर।',
  'onboarding.choice.everything': 'कनेक्टेड कैलेंडर पर सब कुछ',
  'onboarding.choice.everythingDesc': 'सर्वाधिक कवरेज। पहले अपनी टीम को बताएँ।',
  'onboarding.doneTitle': 'आप सेट हो गए',
  'onboarding.doneBody': 'आपका पहला सारांश मीटिंग समाप्त होने के लगभग तीन मिनट बाद आता है। तब तक देखने के लिए कुछ नहीं है — यह अपेक्षित है।',
  'onboarding.goToWorkspace': 'अपने कार्यस्थल पर जाएँ',
  'onboarding.toast.addressCopied': 'पता कॉपी किया गया',
  'ssoCallback.title': 'साइन-इन पूरा किया जा रहा है',
  'ssoCallback.noCode': 'पहचान प्रदाता ने साइन-इन कोड नहीं लौटाया।',
  'ssoCallback.couldNotComplete': 'साइन-इन पूरा नहीं किया जा सका। कृपया फिर से शुरू करें।',
  'ssoCallback.errorTitle': 'हम आपको साइन इन नहीं कर सके',
  'ssoCallback.errorSuffix': ' साइन-इन पृष्ठ पर वापस जाएँ और फिर से प्रयास करें।',
  'ssoCallback.verifying': 'आपके कार्यस्थल खाते की पुष्टि की जा रही है…',

  /* --- misc / error pages --------------------------------------------------- */
  'error.title': 'यह स्क्रीन क्रैश हो गई',
  'error.body': 'कुछ भी नहीं खोया। जारी रखने के लिए रीलोड करें — और यदि यह बार-बार होता है तो नीचे का विवरण हमें भेजें।',
  'error.reload': 'रीलोड करें',
  'error.unknown': 'अज्ञात त्रुटि',
  'notFound.title': 'वह पृष्ठ मौजूद नहीं है',
  'notFound.body': 'लिंक पुराना हो सकता है, या मीटिंग हटा दी गई हो सकती है।',
  'notFound.back': 'अपने कार्यस्थल पर वापस',

  /* --- shared summary --------------------------------------------------------- */
  'shared.badge': 'साझा सारांश',
  'shared.participants': '{count} प्रतिभागी',
  'shared.discussion': 'चर्चा',
  'shared.decisions': 'निर्णय',
  'shared.actionItems': 'कार्य सूची',
  'shared.unassigned': 'असाइन नहीं',
  'shared.inferredSuffix': ' (अनुमानित)',
  'shared.noSummary': 'इस मीटिंग का कोई सारांश नहीं है।',
  'shared.footer': 'एक सभा कार्यस्थल से साझा किया गया। इस लिंक वाला कोई भी यह पृष्ठ पढ़ सकता है।',

  /* --- auth layout marketing --------------------------------------------- */
  'authLayout.quote': 'हर कोई कॉल से कुछ अलग याद रखते हुए निकलता है। सभा वह सब याद रखता है।',
  'authLayout.quoteFooter': 'Teams, Meet और Zoom — जिस भी भाषा में कमरा वास्तव में बोलता है।',
  'authLayout.point1.title': 'हिंदी, अंग्रेज़ी, दोनों',
  'authLayout.point1.detail': 'वाक्य के बीच में बदलें। यह साथ रहता है।',
  'authLayout.point2.title': 'निर्णय, कार्यवृत्त नहीं',
  'authLayout.point2.detail': 'क्या तय हुआ, और अगला कदम किसका है।',
  'authLayout.point3.title': 'केवल आपका',
  'authLayout.point3.detail': 'आपकी शर्तों पर रिकॉर्ड किया गया, आपके कार्यस्थल के भीतर रखा गया।',

  /* --- settings persistence hook / meetings hook --------------------------- */
  'settings.toast.invitationSent': 'आमंत्रण भेजा गया',
  'settings.toast.couldNotSendInvitation': 'वह आमंत्रण नहीं भेज सके',
  'settings.toast.couldNotUpdateRule': 'उस नियम को अपडेट नहीं कर सके',
  'settings.toast.exportQueued': 'निर्यात कतार में — तैयार होने पर हम एक लिंक ईमेल करेंगे',
  'settings.toast.couldNotQueueExport': 'उस निर्यात को कतार में नहीं डाल सके',
  'meetings.toast.calendarNotConnectedTitle': 'कैलेंडर कनेक्ट नहीं है',
  'meetings.toast.calendarNotConnectedBody': 'केवल वही मीटिंग सूचीबद्ध हो सकती हैं जिनका रिकॉर्डर ने पहले ही दावा कर लिया है।',
  'meetings.toast.calendarUnreachableTitle': 'कैलेंडर तक नहीं पहुँच सके',
  'meetings.toast.calendarUnreachableBody': 'हमें मिला अंतिम उत्तर दिखा रहे हैं। एक क्षण में फिर से प्रयास करें।',
  'meetings.toast.refreshFail': 'आगामी मीटिंग रीफ़्रेश नहीं कर सके',
}
